import { useState, useRef, useCallback, useLayoutEffect, useEffect } from 'react';
import { Portal } from 'react-portal';
import { useFilters } from 'components/Dashboard/Filters/FiltersContext';

const TAG_GAP_PX = 8;
const TAG_TEXT_MAX_WIDTH_PX = 160;

const InteractionTags = ({ tags }) => {
  const [showPopup, setShowPopup] = useState(false);
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });
  const [visibleCount, setVisibleCount] = useState(tags.length);
  const [hiddenCount, setHiddenCount] = useState(0);
  const [moreLeftPosition, setMoreLeftPosition] = useState(0);
  const [pillHeight, setPillHeight] = useState(null);
  const hiddenRowRef = useRef(null);
  const hiddenMoreRef = useRef(null);
  const moreButtonRef = useRef(null);
  const popupRef = useRef(null);
  const { filtersState } = useFilters();

  // The popup is portaled to <body> so it can't be clipped by this card's scroll container or
  // sit behind neighbouring cards/sidebar content - that also means it's no longer a descendant
  // of the trigger button, so outside-click detection has to check both refs itself.
  useEffect(() => {
    if (!showPopup) return;
    const closeIfOutside = (event) => {
      const target = event.target;
      if (moreButtonRef.current?.contains(target) || popupRef.current?.contains(target)) return;
      setShowPopup(false);
    };
    const closePopup = () => setShowPopup(false);
    document.addEventListener('mousedown', closeIfOutside);
    const scrollParent = moreButtonRef.current?.closest('.custom-scrollbars__content');
    scrollParent?.addEventListener('scroll', closePopup);
    window.addEventListener('resize', closePopup);
    return () => {
      document.removeEventListener('mousedown', closeIfOutside);
      scrollParent?.removeEventListener('scroll', closePopup);
      window.removeEventListener('resize', closePopup);
    };
  }, [showPopup]);

  const tagFilter = filtersState.find(
    (item) => item.name === 'Tag' && item.filterName === 'tags' && item.pages?.includes('CrmInteractions')
  );
  const isTagSelected = (tag) => !!tagFilter?.options?.find((option) => option.name === tag && option.selected);

  // Active (filter-selected) tags first, so they stay visible instead of getting buried behind
  // "+N more" - the reason someone applied that filter is usually to spot it on each result.
  const sortedTags = [...tags].sort((a, b) => Number(isTagSelected(b)) - Number(isTagSelected(a)));

  // Measured against an always-fully-rendered, invisible copy of every tag plus a "+N more" chip
  // (kept separate from the visible row) so the fit calculation never has to chase its own tail
  // across re-renders, and reserves the chip's actual width rather than a guessed constant.
  const measure = useCallback(() => {
    if (!hiddenRowRef.current) return;
    const pills = Array.from(hiddenRowRef.current.querySelectorAll('[data-tag-pill]'));
    if (pills.length === 0) return;

    // A real tag pill (icon + text) renders taller than the icon-less "+N more" chip at the same
    // font size - force every chip to this measured height instead of guessing at the CSS cause.
    setPillHeight(pills[0].getBoundingClientRect().height);

    const firstRowTop = pills[0].offsetTop;
    let firstRowPills = pills.filter((pill) => pill.offsetTop === firstRowTop);
    const containerWidth = hiddenRowRef.current.clientWidth;
    const moreChipWidth = hiddenMoreRef.current?.getBoundingClientRect().width ?? 90;

    if (firstRowPills.length < pills.length) {
      while (firstRowPills.length > 1) {
        const last = firstRowPills[firstRowPills.length - 1];
        const rightEdge = last.offsetLeft + last.getBoundingClientRect().width;
        if (containerWidth - rightEdge >= moreChipWidth) break;
        firstRowPills = firstRowPills.slice(0, -1);
      }
    }

    const count = firstRowPills.length;
    setVisibleCount(count);
    setHiddenCount(pills.length - count);
    if (count < pills.length) {
      const last = firstRowPills[count - 1];
      setMoreLeftPosition(last.offsetLeft + last.getBoundingClientRect().width + TAG_GAP_PX);
    }
  }, [sortedTags]);

  useLayoutEffect(() => {
    measure();
  }, [measure]);

  useEffect(() => {
    const row = hiddenRowRef.current;
    if (!row || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(row);
    return () => observer.disconnect();
  }, [measure]);

  const toggleMoreTagsPopup = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = moreButtonRef.current?.getBoundingClientRect();
    if (rect) setPopupPosition({ top: rect.bottom + 4, left: rect.left });
    setShowPopup((prev) => !prev);
  };

  const renderPill = (tag) => (
    <div
      key={tag}
      data-tag-pill
      className='rounded-pill border bg-light-blue-5 paragraph-p1 specific-x-padding-13 specific-y-padding-2 light-60-text mr-2'
      style={{
        display: 'flex',
        alignItems: 'center',
        height: pillHeight ?? undefined,
        ...(isTagSelected(tag) ? { boxShadow: '0 0 0 1.5px #0094cc', color: '#0094cc' } : undefined),
      }}
    >
      <span className='icon-tag mr-1' />
      <span
        style={{
          maxWidth: `${TAG_TEXT_MAX_WIDTH_PX}px`,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {tag}
      </span>
    </div>
  );

  const visibleTags = sortedTags.slice(0, visibleCount);
  const hiddenTags = sortedTags.slice(visibleCount);

  return (
    <div className='position-relative' style={{ minWidth: 0, flex: '1 1 0%' }}>
      <div
        ref={hiddenRowRef}
        className='d-flex flex-wrap position-absolute w-100'
        style={{ visibility: 'hidden', top: 0, left: 0, pointerEvents: 'none' }}
        aria-hidden='true'
      >
        {sortedTags.map((tag) => renderPill(tag))}
        {/* Never narrower than the real "+N more" chip, since actual hidden count <= tags.length */}
        <button
          type='button'
          ref={hiddenMoreRef}
          className='general-button rounded-pill border bg-light-blue-5 paragraph-p1 specific-x-padding-13 specific-y-padding-2 light-60-text'
          style={{ display: 'flex', alignItems: 'center', height: pillHeight ?? undefined, whiteSpace: 'nowrap' }}
        >
          +{sortedTags.length} more
        </button>
      </div>
      <div className='d-flex flex-wrap position-relative'>
        {visibleTags.map((tag) => renderPill(tag))}
        {hiddenCount > 0 && (
          <div className='position-absolute' style={{ left: `${moreLeftPosition}px`, top: 0 }}>
            <button
              type='button'
              ref={moreButtonRef}
              className='general-button rounded-pill border bg-light-blue-5 paragraph-p1 specific-x-padding-13 specific-y-padding-2 light-60-text'
              style={{ display: 'flex', alignItems: 'center', height: pillHeight ?? undefined, whiteSpace: 'nowrap' }}
              onClick={toggleMoreTagsPopup}
            >
              +{hiddenCount} more
            </button>
          </div>
        )}
      </div>
      {showPopup && (
        <Portal node={document && document.querySelector('body')}>
          <div
            ref={popupRef}
            className='box box-shadow bg-main-white position-fixed p-2'
            style={{ top: `${popupPosition.top}px`, left: `${popupPosition.left}px`, zIndex: 100000 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className='d-flex flex-column align-items-start' style={{ gap: '6px' }}>
              {hiddenTags.map((tag) => renderPill(tag))}
            </div>
          </div>
        </Portal>
      )}
    </div>
  );
};

export default InteractionTags;
